/* ==========================================================================
   POUCHLAND Product Cards - Global JavaScript
   Handles quantity selection and AJAX add to cart for all product cards
   ========================================================================== */

(function() {
  'use strict';

  /**
   * Format price in Euros
   */
  function formatMoney(amountInCents) {
    const amount = (amountInCents / 100).toFixed(2).replace('.', ',');
    return '€' + amount;
  }

  /**
   * Get discount percentages from card data attributes
   */
  function getDiscounts(card) {
    return {
      1: 0,
      5: parseInt(card.dataset.discount5) || 5,
      10: parseInt(card.dataset.discount10) || 10,
      30: parseInt(card.dataset.discount30) || 15
    };
  }

  /**
   * Update pricing display when quantity changes
   */
  function updatePricing(select) {
    const card = select.closest('.product-card');
    if (!card) return;

    const quantity = parseInt(select.value);
    const basePrice = parseFloat(card.dataset.basePrice); // In decimal (e.g., 4.99)
    const comparePrice = card.dataset.comparePrice ? parseFloat(card.dataset.comparePrice) : null;
    const discounts = getDiscounts(card);
    const discount = discounts[quantity] || 0;

    // Calculate prices (in decimal)
    const discountedUnitPrice = basePrice * (1 - discount / 100);
    const totalPrice = discountedUnitPrice * quantity;

    // Get display elements
    const priceDisplay = card.querySelector('.product-card__price');
    const perCanDisplay = card.querySelector('.product-card__per-can');
    const comparePriceDisplay = card.querySelector('.product-card__compare-price');
    const quantityLabel = card.querySelector('.product-card__quantity-label');
    const addBtn = card.querySelector('.product-card__add-btn');

    // Update displays (convert to cents for formatMoney)
    if (priceDisplay) priceDisplay.textContent = formatMoney(totalPrice * 100);
    if (perCanDisplay) perCanDisplay.textContent = formatMoney(discountedUnitPrice * 100) + '/can';
    if (quantityLabel) quantityLabel.innerHTML = '<strong>' + quantity + '</strong> ' + (quantity === 1 ? 'can' : 'cans');
    if (addBtn) addBtn.dataset.quantity = quantity;

    // Only show strikethrough for actual sales (compare_at_price > price)
    const hasComparePrice = comparePrice && comparePrice > basePrice;

    if (hasComparePrice) {
      const originalTotal = comparePrice * quantity;

      if (comparePriceDisplay) {
        comparePriceDisplay.textContent = formatMoney(originalTotal * 100);
        comparePriceDisplay.style.display = 'inline';
      }
      if (priceDisplay) priceDisplay.classList.add('product-card__price--sale');
    } else {
      if (comparePriceDisplay) comparePriceDisplay.style.display = 'none';
      if (priceDisplay) priceDisplay.classList.remove('product-card__price--sale');
    }
  }

  /**
   * Add to cart functionality
   */
  async function addToCart(btn) {
    const card = btn.closest('.product-card');
    if (!card) return;

    const variantId = card.dataset.variantId;
    const quantity = parseInt(btn.dataset.quantity) || 1;

    btn.classList.add('product-card__add-btn--loading');
    btn.disabled = true;

    // Dawn cart drawer / notification: ask Shopify to render their sections in the same request
    const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const body = { id: variantId, quantity: quantity };
    if (cartEl && typeof cartEl.getSectionsToRender === 'function' && typeof cartEl.renderContents === 'function') {
      body.sections = cartEl.getSectionsToRender().map((s) => s.id).join(',');
      body.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add to cart failed');

      if (cartEl && body.sections) {
        // Dawn keeps `is-empty` on <cart-drawer> after the first add, which breaks its focus trap
        cartEl.classList.remove('is-empty');
        cartEl.renderContents(data);
      } else {
        updateCartCount();
      }

      btn.classList.remove('product-card__add-btn--loading');
      btn.classList.add('product-card__add-btn--success');
      const originalHTML = btn.innerHTML;
      btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Added';
      setTimeout(() => {
        btn.classList.remove('product-card__add-btn--success');
        btn.innerHTML = originalHTML;
        btn.disabled = false;
      }, 2000);

      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));
    } catch (error) {
      console.error('Add to cart error:', error);
      btn.classList.remove('product-card__add-btn--loading');
      btn.classList.add('product-card__add-btn--error');
      setTimeout(() => {
        btn.classList.remove('product-card__add-btn--error');
        btn.disabled = false;
      }, 2000);
    }
  }

  /**
   * Update cart count in header
   */
  async function updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Dawn theme: find cart bubble and update
      const cartBubble = document.querySelector('.cart-count-bubble');
      if (cartBubble) {
        const countSpan = cartBubble.querySelector('span[aria-hidden="true"]');
        if (countSpan) countSpan.textContent = cart.item_count;
        
        // Show bubble if hidden (first item added)
        if (cart.item_count > 0) {
          cartBubble.removeAttribute('hidden');
          cartBubble.style.display = '';
          cartBubble.classList.remove('hidden');
        }
      }

      // Try to open cart drawer if theme has one
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }

  /**
   * Initialize event listeners
   */
  function init() {
    // Quantity change handler (delegated)
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('product-card__quantity-select')) {
        updatePricing(e.target);
      }
    });

    // Add to cart handler (delegated)
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.product-card__add-btn');
      if (btn && !btn.disabled) {
        e.preventDefault();
        addToCart(btn);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init for Shopify section events
  document.addEventListener('shopify:section:load', init);

})();